/**
 * First-class draft review card. Replaces the legacy chip-row + DraftChangeRow.
 * Lean-but-better than the old UI: header summary + destructive warning,
 * per-change expandable before/after preview, Apply/Decline with an applying
 * spinner and inline error. Changes stay granular so per-change accept is an
 * additive fast-follow.
 */

import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import type { AiDraft, AiDraftChange } from '@/shared/types/chat';
import type { TextFitWarning } from '@/features/slides/utils/textFit';

function getChangeIcon(change: AiDraftChange) {
  const operation =
    typeof change.payload.operation === 'string' ? change.payload.operation.toLowerCase() : '';
  if (change.destructive || operation === 'delete') return <DeleteOutlineIcon fontSize="small" />;
  if (operation === 'create') return <AddCircleOutlineIcon fontSize="small" />;
  if (operation === 'update' || operation === 'patch_document' || operation === 'patch_slides')
    return <EditOutlinedIcon fontSize="small" />;
  return <BuildOutlinedIcon fontSize="small" />;
}

const ChangeRow: React.FC<{ change: AiDraftChange }> = ({ change }) => {
  const [expanded, setExpanded] = useState(false);
  const icon = useMemo(() => getChangeIcon(change), [change]);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_e, next) => setExpanded(next)}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Box sx={{ color: change.destructive ? 'warning.main' : 'text.secondary', mt: 0.1 }}>
            {icon}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {change.label}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', overflowWrap: 'anywhere' }}
            >
              {change.targetLabel}
            </Typography>
          </Box>
          {change.destructive && <Chip size="small" color="warning" label="Destructive" />}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {change.preview.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No preview details available.
          </Typography>
        ) : (
          change.preview.map((entry, index) => (
            <Box key={`${change.changeId}-preview-${index}`} sx={{ mb: 0.4 }}>
              {entry.before !== undefined ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  <strong>{entry.label}:</strong>{' '}
                  <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>
                    {entry.before}
                  </span>
                  {' → '}
                  {entry.after ?? ''}
                </Typography>
              ) : entry.after ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  <strong>{entry.label}:</strong> {entry.after}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block">
                  {entry.label}
                </Typography>
              )}
            </Box>
          ))
        )}
      </AccordionDetails>
    </Accordion>
  );
};

interface DraftReviewCardProps {
  draft: AiDraft;
  applying: boolean;
  applyError: string | null;
  warnings?: TextFitWarning[];
  onApply: () => void;
  onReject: () => void;
}

const DraftReviewCard: React.FC<DraftReviewCardProps> = ({
  draft,
  applying,
  applyError,
  warnings,
  onApply,
  onReject,
}) => {
  const count = draft.changes.length;
  const overflowWarningCount = useMemo(
    () => (warnings ?? []).filter((w) => w.kind === 'still_overflows').length,
    [warnings]
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: draft.hasDestructive ? 'warning.main' : 'primary.light',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
          Proposed changes ({count})
        </Typography>
        {draft.hasDestructive && (
          <Chip
            size="small"
            color="warning"
            icon={<WarningAmberIcon />}
            label="Includes destructive changes"
          />
        )}
      </Box>

      {overflowWarningCount > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Text on {overflowWarningCount} slide element
          {overflowWarningCount === 1 ? '' : 's'} may still be clipped at the minimum font size.
          Review after applying.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {draft.changes.map((change) => (
          <ChangeRow key={change.changeId} change={change} />
        ))}
      </Box>

      {applyError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {applyError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={applying}
          onClick={onApply}
          startIcon={applying ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {applying ? 'Applying…' : 'Apply'}
        </Button>
        <Button variant="outlined" size="small" disabled={applying} onClick={onReject}>
          Decline
        </Button>
      </Box>
    </Paper>
  );
};

export default React.memo(DraftReviewCard);
